import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft } from 'react-icons/fa';
import { Alert, Button } from 'react-bootstrap';
import payphoneImg from '../assets/payphone.png';
import './Pasarela.css';
import DepositModal from '../components/DepositModal';
import DeUnaModal from '../components/DeUnaModal';
import { Browser } from '@capacitor/browser';

const PAYPHONE_BACKEND = 'https://nuevo-payphonesisnetel.u5r75b.easypanel.host';

interface ApiInvoiceResponse {
  estado: string;
  factura: {
    id: number;
    idcliente: number;
    total: string;
    estado: string;
    emitido: string;
    vencimiento: string;
    urlpdf: string;
  };
}

interface ApiClientResponse {
  estado: string;
  datos: Array<{ nombre: string; estado: string; cedula?: string }>;
}

interface Factura {
  id: number;
  idcliente: number;
  total: number;
  estado: string;
  emitido: string;
  vencimiento: string;
  urlpdf: string;
}

const Pasarela: React.FC = () => {
  const { facturaId } = useParams<{ facturaId: string }>();
  const navigate = useNavigate();

  const sessionRef = useRef<string>('');
  const [factura, setFactura] = useState<Factura | null>(null);
  const [clienteNombre, setClienteNombre] = useState('');
  const [clienteEstado, setClienteEstado] = useState('');
  const [clienteCedula, setClienteCedula] = useState('');
  const [cajitaMostrada, setCajitaMostrada] = useState(false);
  const [mostrarPayphoneBtn, setMostrarPayphoneBtn] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDeUnaModal, setShowDeUnaModal] = useState<boolean>(false);

  // Mostrar botón PayPhone si se indicó antes de recargar
  useEffect(() => {
    if (sessionStorage.getItem('mostrarPayphoneBtn') === 'true') {
      setMostrarPayphoneBtn(true);
      sessionStorage.removeItem('mostrarPayphoneBtn');
    }
  }, []);

  const N8N_DETALLE  = process.env.REACT_APP_N8N_URL_FACTURA_DETALLE!;
  const N8N_CLIENTE  = process.env.REACT_APP_N8N_URL_CLIENTE_CEDULA!;

  // Carga de factura via N8N proxy
  useEffect(() => {
    if (!facturaId) return;
    axios.post<ApiInvoiceResponse>(
      N8N_DETALLE,
      { idfactura: Number(facturaId) },
      { headers: { 'Content-Type': 'application/json' } }
    )
    .then(({ data }) => {
      const f = data.factura;
      setFactura({
        id: f.id,
        idcliente: f.idcliente,
        total: parseFloat(f.total),
        estado: f.estado,
        emitido: f.emitido,
        vencimiento: f.vencimiento,
        urlpdf: f.urlpdf,
      });
    })
    .catch(() => {});
  }, [facturaId]);

  // Carga de cliente via N8N proxy
  useEffect(() => {
    if (!factura) return;
    axios.post<ApiClientResponse>(
      N8N_CLIENTE,
      { idcliente: factura.idcliente },
      { headers: { 'Content-Type': 'application/json' } }
    )
    .then(({ data }) => {
      if (data.datos.length > 0) {
        setClienteNombre(data.datos[0].nombre);
        setClienteEstado(data.datos[0].estado.toUpperCase());
        if (data.datos[0].cedula) setClienteCedula(data.datos[0].cedula);
      }
    })
    .catch(() => {});
  }, [factura]);

  if (!factura) return <p>Cargando factura…</p>;

  const comision = +(factura.total * 0.06).toFixed(2);
  const totalConComision = +(factura.total + comision).toFixed(2);
  const isPagada = factura.estado.trim().toLowerCase() === 'pagado';

  const estadoBadge = (
    <span className={`badge ${clienteEstado === 'ACTIVO' ? 'bg-success' : 'bg-danger'}`} style={{ minWidth: 75 }}>
      {clienteEstado}
    </span>
  );

  const generarTransactionId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `sisnetel_${timestamp}_${random}`;
  };

  const abrirCajitaPayPhone = async () => {
    if (!factura || cajitaMostrada) return;
    setCajitaMostrada(true);
    const session = generarTransactionId();
    sessionRef.current = session;
    const montoEnCentavos = Math.round(totalConComision * 100);
    const url = `${PAYPHONE_BACKEND}/pagar?session=${session}&monto=${montoEnCentavos}&factura=${factura.id}&clienteId=${factura.idcliente}&cedula=${encodeURIComponent(clienteCedula)}`;
    await Browser.open({ url, presentationStyle: 'popover' });

    // Cuando el browser se cierra, consultar estado del pago
    Browser.addListener('browserFinished', async () => {
      Browser.removeAllListeners();
      try {
        const res  = await fetch(`${PAYPHONE_BACKEND}/api/estado?session=${sessionRef.current}`);
        const data = await res.json();
        if (data.status === 'aprobado' && data.payphoneId) {
          navigate(`/confirmacionpago?id=${data.payphoneId}&clientTransactionId=${data.clientTxId}`);
        }
      } catch {}
    });
  };

  const handlePagarAhora = () => {
    sessionStorage.setItem('mostrarPayphoneBtn', 'true');
    window.location.reload();
  };

  const handlePayPhoneClick = () => {
    if (!cajitaMostrada) abrirCajitaPayPhone();
  };
 const handleDeUnaClick = (): void => {
    setShowDeUnaModal(true);
  };

   // Handler para cerrar el modal DeUna
  const handleCloseDeUna = (): void => {
    setShowDeUnaModal(false);
  };
  return (
    <div className="pasarela-page container mt-4">
      {!isPagada && (
        <button className="btn-back" onClick={() => navigate(-1)}>
          <div className="icon-circle"><FaArrowLeft/></div>
          <div className="back-text">Atrás</div>
        </button>
      )}

      <div id="pp-button-container" className="pp-fixed-box" />

      {clienteNombre && (
        <div className="cliente-nombre mb-2 d-flex align-items-center gap-2">
          {estadoBadge}
          <strong>{clienteNombre}</strong>
        </div>
      )}

      <h4 className="mb-4">Pasarela de pago – Factura #{factura.id}</h4>

      <table className="table table-bordered text-center pasarela-data-table mb-4">
        <thead>
          <tr>
            <th>Emitido</th>
            <th>Vencimiento</th>
            <th>Total a pagar</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{factura.emitido}</td>
            <td>{factura.vencimiento}</td>
            <td><strong>${factura.total.toFixed(2)}</strong></td>
            <td>{factura.estado}</td>
          </tr>
        </tbody>
      </table>

      <p className="mb-4">
        <a href={factura.urlpdf} target="_blank" rel="noopener noreferrer">
          Ver PDF de la factura
        </a>
      </p>

      {isPagada ? (
        <div className="text-center my-4">
          <Alert variant="success">
            🎉 ¡Gracias por tu pago! Tu factura #{factura.id} está al día.
          </Alert>
          <Button variant="primary" onClick={() => navigate('/menu')}>Inicio</Button>
        </div>
      ) : (
        <>
          {!mostrarPayphoneBtn && (
            <div className="d-flex align-items-center gap-3 mb-2">
              <button className="btn btn-success" onClick={handlePagarAhora}>
                Pagar Ahora
              </button>
            </div>
          )}
            {/* 2. Ahora mostramos **ambos** botones juntos, solo tras recarga */}
 {mostrarPayphoneBtn && (
   <div className="d-flex align-items-center gap-3 mb-2" id="pp-button">
     {/* 1️ Botón DeUna */}
    <button
      className="btn btn-secondary"
      onClick={handleDeUnaClick}
      aria-label="DeUna"
    >
      DeUna
    </button> 
 
	 {/* 2 Pagar con PayPhone */}
     <button className="payphone-btn btn btn-primary" onClick={handlePayPhoneClick} aria-label="Pagar con PayPhone">
       <img src={payphoneImg} alt="PayPhone" className="img-btn" style={{ width: 24, marginRight: 8 }} />
       Pagar con PayPhone
     </button>

     {/* 3 Subir Depósito (abre el modal) */}
     <button
       type="button"
       className="btn btn-outline-primary"
       onClick={() => setShowDepositModal(true)}
     >
       Subir Depósito
     </button>
   </div>
 )}



        </>
      )}

     

  
  {/* ——— Aquí insertas tu modal independiente ——— */}
 <DepositModal
   show={showDepositModal}
   onClose={() => {
     // cierra el modal...
     setShowDepositModal(false);
     // ...y vuelve a /pasarela/<id> recargando la página
     navigate(`/pasarela/${factura!.id}`);
     window.location.reload();
   }}
   factura={factura!}
   clienteNombre={clienteNombre}
   clienteEstado={clienteEstado}
 />
  
  {/* ——— Aquí insertas tu modal independiente ——— */}
 
{showDeUnaModal && (
  <DeUnaModal
    show={showDeUnaModal}
    onClose={handleCloseDeUna}
    deUnaUrl="https://n8n.sisnetel.com/webhook/pagos"
    clienteNombre={clienteNombre}
    clienteEstado={clienteEstado}
    factura={factura!}
  />
)}



      <div className="payphone-card shadow-sm mt-3">
        <div className="d-flex align-items-center mb-2">
          <span className="payphone-title me-2">PAYPHONE</span>
          <span className="text-muted small">Visa / MasterCard 💳</span>
        </div>
        <p className="mb-2 small">Pagos con tarjeta de crédito tienen una comisión adicional del <strong>6 %</strong>.</p>
        <ul className="list-unstyled mb-0">
          <li className="d-flex justify-content-between">
            <span>Monto:</span><strong>${factura.total.toFixed(2)}</strong>
          </li>
          <li className="d-flex justify-content-between">
            <span>Total (6 % comisión):</span><strong>${totalConComision.toFixed(2)}</strong></li>
        </ul>
      </div>
    </div>
  );
};

export default Pasarela;
